import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useChildStore } from '@/lib/store/child'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { 
  ArrowLeft, 
  CalendarIcon, 
  Save, 
  Loader2, 
  AlertTriangle, 
  Check,
  Utensils
} from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import { ActivityType } from "@/types/activity"
import { fetchAllActivityData, updateActivity } from "@/lib/services/activity-service"
import { formatLocaleDateString, formatDateToUTC, parseDate } from "@/lib/utils/date"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { ReferenceItem } from "@/types/activity"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { format, parseISO, add, isValid } from 'date-fns'
import { cn } from "@/lib/utils"
import dayjs from 'dayjs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

/**
 * Edit Activity page component
 * Allows editing of sleep, nap, and feeding activities
 */
export function EditActivity() {
  const navigate = useNavigate()
  const { type, id } = useParams<{ type: 'sleep' | 'nap' | 'feeding', id: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { selectedChild } = useChildStore()
  
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activity, setActivity] = useState<ActivityType | null>(null)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [timeInputsValid, setTimeInputsValid] = useState(true)
  
  // For editable fields
  const [selectedQuality, setSelectedQuality] = useState<string | null>(null)
  const [selectedSound, setSelectedSound] = useState<string | null>(null)
  const [selectedSoundLevel, setSelectedSoundLevel] = useState<string | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [selectedEnvironment, setSelectedEnvironment] = useState<string | null>(null)
  const [selectedFeedingType, setSelectedFeedingType] = useState<string | null>(null)
  const [bottleAmountOz, setBottleAmountOz] = useState<string>('')
  const [formulaAmountOz, setFormulaAmountOz] = useState<string>('')
  const [waterTemp, setWaterTemp] = useState<string | null>(null)
  const [selectedBreast, setSelectedBreast] = useState<string | null>(null)
  const [feedingDuration, setFeedingDuration] = useState<string>('')
  const [feedingUnit, setFeedingUnit] = useState<string>('oz')
  
  // For date/time editing
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [startTime, setStartTime] = useState<string>('')
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [endTime, setEndTime] = useState<string>('')

  // Helper to calculate and format the duration between start and end times
  const calculateDuration = () => {
    if (!startDate || !startTime || !endDate || !endTime) return '';
    
    // Create date objects with the selected dates and times
    const start = new Date(startDate);
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    start.setHours(startHours, startMinutes, 0, 0);
    
    const end = new Date(endDate);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    end.setHours(endHours, endMinutes, 0, 0);
    
    // Calculate duration in milliseconds
    const durationMs = end.getTime() - start.getTime();
    
    // Handle edge cases
    if (durationMs < 0) return 'Invalid duration (end before start)';
    if (durationMs === 0) return '0 minutes';
    
    // Convert to hours and minutes
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    // Format the duration string
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  // Fetch reference data for dropdowns
  const { data: qualityOptions } = useQuery({
    queryKey: ['restfulness_options', type],
    queryFn: async () => {
      try {
        // Different tables for sleep vs nap based on the schema
        if (type === 'nap') {
          // For nap sessions, use restfulness_rating table with description field
          const { data, error } = await supabase
            .from('restfulness_rating')
            .select('id, description')
          
          if (error) throw error
          
          return (data || []).map(item => ({
            id: item.id,
            name: item.description
          })) as ReferenceItem[]
        } else {
          // For sleep sessions, use restfulness table with rating field
          const { data, error } = await supabase
            .from('restfulness')
            .select('id, rating')
          
          if (error) throw error
          
          return (data || []).map(item => ({
            id: item.id,
            name: item.rating
          })) as ReferenceItem[]
        }
      } catch (error) {
        console.error('Error fetching quality options:', error)
        return []
      }
    },
    enabled: !!type
  })

  const { data: soundOptions } = useQuery({
    queryKey: ['sound_options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sleep_sounds')
        .select('id, name')
      
      if (error) throw error
      return data.map(item => ({
        id: item.id,
        name: item.name
      })) as ReferenceItem[]
    },
    enabled: type === 'sleep'
  })

  const { data: soundLevelOptions } = useQuery({
    queryKey: ['sound_level_options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sound_levels')
        .select('id, level')
      
      if (error) throw error
      return data.map(item => ({
        id: item.id,
        name: item.level
      })) as ReferenceItem[]
    },
    enabled: type === 'sleep'
  })

  const { data: locationOptions } = useQuery({
    queryKey: ['location_options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nap_location')
        .select('id, description')
      
      if (error) throw error
      return data.map(item => ({
        id: item.id,
        name: item.description
      })) as ReferenceItem[]
    },
    enabled: type === 'nap' || type === 'sleep'
  })

  const { data: methodOptions } = useQuery({
    queryKey: ['method_options', type],
    queryFn: async () => {
      try {
        // Different tables for sleep vs nap based on the schema
        if (type === 'nap') {
          const { data, error } = await supabase
            .from('sleep_onset_method')
            .select('id, description')
          
          if (error) throw error
          
          return (data || []).map(item => ({
            id: item.id,
            name: item.description
          })) as ReferenceItem[]
        } else {
          // Sleep type
          const { data, error } = await supabase
            .from('onset_methods')
            .select('id, method')
          
          if (error) throw error
          
          return (data || []).map(item => ({
            id: item.id,
            name: item.method
          })) as ReferenceItem[]
        }
      } catch (error) {
        console.error('Error fetching method options:', error)
        return []
      }
    },
    enabled: type === 'sleep' || type === 'nap'
  })

  const { data: environmentOptions } = useQuery({
    queryKey: ['environment_options', type],
    queryFn: async () => {
      try {
        // Different environment tables for sleep vs nap
        if (type === 'nap') {
          // For nap sessions, use sleep_environment table
          const { data, error } = await supabase
            .from('sleep_environment')
            .select('id, description')
          
          if (error) throw error
          
          return (data || []).map(item => ({
            id: item.id,
            name: item.description
          })) as ReferenceItem[]
        } else {
          // For sleep sessions, use environments table
          const { data, error } = await supabase
            .from('environments')
            .select('id, description')
          
          if (error) throw error
          
          return (data || []).map(item => ({
            id: item.id,
            name: item.description
          })) as ReferenceItem[]
        }
      } catch (error) {
        console.error('Error fetching environment options:', error)
        return []
      }
    },
    enabled: type === 'sleep' || type === 'nap'
  })

  const { data: feedingTypeOptions } = useQuery({
    queryKey: ['feeding_type_options'],
    queryFn: async () => {
      return [
        { id: 'breastfeeding', name: 'Breast' },
        { id: 'bottle', name: 'Bottle' },
        { id: 'formula', name: 'Formula' },
        { id: 'solid', name: 'Solid Food' }
      ] as ReferenceItem[]
    },
    enabled: type === 'feeding'
  })

  const { data: breastOptions } = useQuery({
    queryKey: ['breast_options'],
    queryFn: async () => {
      return [
        { id: 'left', name: 'Left' },
        { id: 'right', name: 'Right' },
        { id: 'both', name: 'Both' }
      ] as ReferenceItem[]
    },
    enabled: type === 'feeding'
  })

  useEffect(() => {
    const loadActivity = async () => {
      try {
        setIsLoading(true)
        
        // Only proceed if we have an ID and type
        if (!id || !type || !selectedChild) {
          throw new Error('Missing activity ID, type or selected child')
        }
        
        // Get all activities and find the one with matching ID
        const data = await fetchAllActivityData(selectedChild.id, {
          activityType: type
        })
        
        let foundActivity: ActivityType | undefined
        
        switch(type) {
          case 'sleep':
            foundActivity = data.sleepActivities.find(a => a.id === id)
            break
          case 'nap':
            foundActivity = data.napActivities.find(a => a.id === id)
            break
          case 'feeding':
            foundActivity = data.feedingActivities.find(a => a.id === id)
            break
        }
        
        if (!foundActivity) {
          throw new Error(`Activity not found: ${id}`)
        }
        
        // Set the activity data
        setActivity(foundActivity)
        setNotes(foundActivity.notes || '')
        
        // Set start and end dates for editing
        if (foundActivity.startTime) {
          try {
            const startDateTime = parseISO(foundActivity.startTime)
            if (isValid(startDateTime)) {
              setStartDate(startDateTime)
              setStartTime(format(startDateTime, 'HH:mm'))
            } else {
              console.error('Invalid start time format:', foundActivity.startTime)
              // Set reasonable defaults if parsing fails
              setStartDate(new Date())
              setStartTime('00:00')
            }
          } catch (error) {
            console.error('Error parsing start time:', error)
            // Set reasonable defaults if parsing fails
            setStartDate(new Date())
            setStartTime('00:00')
          }
        }
        
        if (foundActivity.endTime) {
          try {
            const endDateTime = parseISO(foundActivity.endTime)
            if (isValid(endDateTime)) {
              setEndDate(endDateTime)
              setEndTime(format(endDateTime, 'HH:mm'))
            } else {
              console.error('Invalid end time format:', foundActivity.endTime)
              // Set end time same as start time if parsing fails
              setEndDate(new Date())
              setEndTime('00:00')
            }
          } catch (error) {
            console.error('Error parsing end time:', error)
            // Set end time same as start time if parsing fails
            setEndDate(new Date())
            setEndTime('00:00')
          }
        }
        
        // Set type-specific fields
        if (type === 'sleep' || type === 'nap') {
          setSelectedQuality(foundActivity.quality || null)
          setSelectedLocation(foundActivity.location || null)
          setSelectedMethod(foundActivity.onset_method || null)
          setSelectedEnvironment(foundActivity.environment || null)
          
          if (type === 'sleep') {
            setSelectedSound(foundActivity.sound || null)
            setSelectedSoundLevel(foundActivity.sound_level || null)
          }
        } else if (type === 'feeding') {
          // Set the feeding type first
          setSelectedFeedingType(foundActivity.feeding_type || null)
          
          // Handle different feeding types
          if (foundActivity.feeding_type === 'breastfeeding') {
            setSelectedBreast(foundActivity.breast || null)
            
            if (foundActivity.duration) {
              setFeedingDuration(foundActivity.duration.toString())
            }
          } else if (foundActivity.feeding_type === 'bottle') {
            // Convert amount to string for the UI
            if (typeof foundActivity.feeding_amount !== 'undefined') {
              setBottleAmountOz(foundActivity.feeding_amount.toString())
            }
          } else if (foundActivity.feeding_type === 'formula') {
            // Convert amount to string for the UI
            if (typeof foundActivity.feeding_amount !== 'undefined') {
              setFormulaAmountOz(foundActivity.feeding_amount.toString())
            }
            
            // Set water temperature
            setWaterTemp(foundActivity.temperature || 'warm')
          }
        }
        
        setError(null)
      } catch (error) {
        console.error('Error loading activity:', error)
        setError('Failed to load activity data')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadActivity()
  }, [selectedChild, type, id])

  const handleBackClick = () => {
    navigate('/activities')
  }

  const handleSaveClick = () => {
    // First check if form is valid
    if (!timeInputsValid) {
      toast({
        title: "Missing Information",
        description: "Please fill out all required fields",
        variant: "destructive"
      })
      return
    }
    
    // Show confirmation dialog
    setShowConfirmation(true)
  }
  
  const handleSave = async () => {
    try {
      setIsSubmitting(true)
      setError(null)
      
      // First, validate that we have the necessary fields
      if (!startDate || !startTime) {
        throw new Error('Start date and time are required')
      }
      
      // For non-feeding activities, we need an end time
      if (type !== 'feeding' && (!endDate || !endTime)) {
        throw new Error('End date and time are required')
      }
      
      // If feeding activity and no duration
      if (type === 'feeding' && selectedFeedingType === 'breastfeeding' && !feedingDuration) {
        throw new Error('Feeding duration is required')
      }
      
      // Create update object
      const updates: any = {
        notes: notes,
      }
      
      // Handle different activity types
      if (type === 'sleep' || type === 'nap') {
        updates.quality = selectedQuality
        updates.location = selectedLocation
        updates.onset_method = selectedMethod
        updates.environment = selectedEnvironment
        
        // Set start and end times
        const formattedStartTime = formatDateTimeForDB(startDate, startTime)
        const formattedEndTime = formatDateTimeForDB(endDate, endTime)
        updates.startTime = formattedStartTime
        updates.endTime = formattedEndTime
        
        if (type === 'sleep') {
          updates.sound = selectedSound
          updates.sound_level = selectedSoundLevel
        }
      } else if (type === 'feeding') {
        // Always include the feeding type
        updates.feeding_type = selectedFeedingType
        
        // Add the start time for all feeding types
        const formattedStartTime = formatDateTimeForDB(startDate, startTime)
        updates.startTime = formattedStartTime
        
        // Handle type-specific fields
        if (selectedFeedingType === 'breastfeeding') {
          updates.breast = selectedBreast
          
          // Parse and set duration for breastfeeding
          const durationInMinutes = parseInt(feedingDuration, 10)
          if (!isNaN(durationInMinutes)) {
            updates.duration = durationInMinutes
            
            // Calculate end time based on duration
            const startDateTime = parseISO(formattedStartTime)
            const endDateTime = add(startDateTime, { minutes: durationInMinutes })
            updates.endTime = format(endDateTime, "yyyy-MM-dd'T'HH:mm:ss")
          } else {
            // If no valid duration, set end time same as start time
            updates.endTime = formattedStartTime
          }
          
          // Make sure bottle/formula specific fields are null
          updates.feeding_amount = null
          updates.unit = null
          updates.temperature = null
        } else if (selectedFeedingType === 'bottle') {
          // Parse and set amount for bottle feeding
          const amountNum = parseFloat(bottleAmountOz)
          if (!isNaN(amountNum)) {
            updates.feeding_amount = amountNum
            updates.unit = 'oz' // Always use oz for the UI
          }
          
          // Set end time same as start time for bottle feeding
          updates.endTime = formattedStartTime
          
          // Make sure breastfeeding specific fields are null
          updates.breast = null
          updates.duration = null
          updates.temperature = null
        } else if (selectedFeedingType === 'formula') {
          // Parse and set amount for formula feeding
          const amountNum = parseFloat(formulaAmountOz)
          if (!isNaN(amountNum)) {
            updates.feeding_amount = amountNum
            updates.unit = 'oz' // Always use oz for the UI
          }
          
          // Save water temperature for formula
          updates.temperature = waterTemp
          
          // Set end time same as start time for formula feeding
          updates.endTime = formattedStartTime
          
          // Make sure breastfeeding specific fields are null
          updates.breast = null
          updates.duration = null
        } else if (selectedFeedingType === 'solid') {
          // Handle solid food feeding type
          // Set end time same as start time for solid feeding
          updates.endTime = formattedStartTime
          
          // Make sure other feeding-specific fields are null
          updates.breast = null
          updates.duration = null
          updates.feeding_amount = null
          updates.unit = null
          updates.temperature = null
        }
      }
      
      // Call the API to update the activity
      await updateActivity(id!, type, updates)
      
      // Show success message and redirect
      toast({
        title: "Success!",
        description: "Activity updated successfully",
        variant: "default"
      })
      
      setTimeout(() => {
        navigate('/activities')
      }, 1500)
    } catch (error: any) {
      console.error('Error saving activity:', error)
      toast({
        title: "Error",
        description: error.message || 'Failed to save activity',
        variant: "destructive"
      })
      setError(error.message || 'Failed to save activity')
    } finally {
      setIsSubmitting(false)
      setShowConfirmation(false)
    }
  }

  // Validate time inputs when they change
  useEffect(() => {
    // Simple validation for required fields
    let valid = true
    
    if (!startDate || !startTime) {
      valid = false
    }
    
    if (type !== 'feeding' && (!endDate || !endTime)) {
      valid = false
    }
    
    if (type === 'feeding' && selectedFeedingType === 'breastfeeding' && !feedingDuration) {
      valid = false
    }
    
    setTimeInputsValid(valid)
  }, [startDate, startTime, endDate, endTime, type, selectedFeedingType, feedingDuration])

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-3xl p-4 md:p-6 flex justify-center items-center min-h-[50vh]">
        <div className="flex flex-col items-center p-8 rounded-lg bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700 shadow-xl">
          <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
          <p className="text-slate-300 font-medium">Loading activity details...</p>
        </div>
      </div>
    )
  }

  if (error || !activity) {
    return (
      <div className="container mx-auto max-w-3xl p-4 md:p-6">
        <div className="mb-6">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleBackClick}
            className="flex items-center gap-2 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Activities
          </Button>
        </div>
        
        <Card className="border-destructive bg-red-900/10 shadow-xl">
          <CardHeader className="bg-red-950/30 rounded-t-lg">
            <CardTitle className="text-destructive flex items-center gap-2 text-red-300">
              <AlertTriangle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-red-300">{error || "Activity not found"}</p>
          </CardContent>
          <CardFooter className="border-t border-red-800/30 pt-4">
            <Button onClick={handleBackClick} variant="destructive" className="bg-red-700 hover:bg-red-600">Return to Activities</Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-3xl p-4 md:p-6">
      <div className="mb-6">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleBackClick}
          className="flex items-center gap-2 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Activities
        </Button>
      </div>
      
      <Card className="border border-slate-700/50 shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 via-purple-900/20 to-slate-800 p-1">
          <CardHeader className="bg-slate-900 rounded-t-md">
            <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              Edit {type.charAt(0).toUpperCase() + type.slice(1)} Activity
            </CardTitle>
            <CardDescription className="text-slate-400">
              Make changes to this activity's details
            </CardDescription>
          </CardHeader>
        </div>
        <CardContent className="space-y-6 pt-6 bg-slate-900">
          {/* Validation errors */}
          {error && (
            <div className="bg-red-900/20 border border-red-700/50 rounded-md p-4 mb-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          )}
          
          {/* Activity-specific sections */}
          <div className="space-y-6">
            {/* Sleep and Nap common fields */}
            {(type === 'sleep' || type === 'nap') && (
              <div className="border border-slate-700/50 rounded-lg p-6 bg-slate-800/30 backdrop-blur-sm">
                <h3 className="text-lg font-medium mb-4 text-blue-300 flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Date & Time
                </h3>
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex-1 space-y-3">
                    <Label className="text-slate-300">Start Date & Time</Label>
                    <div className="flex space-x-2">
                      <div className="flex-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal border-slate-600 bg-slate-800 hover:bg-slate-700",
                                !startDate && "text-slate-400"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-blue-400" />
                              {startDate ? format(startDate, "PP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 border-slate-700 bg-slate-800">
                            <Calendar
                              mode="single"
                              selected={startDate}
                              onSelect={setStartDate}
                              initialFocus
                              className="bg-slate-800 text-white"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="w-24">
                        <Input 
                          type="time" 
                          value={startTime} 
                          onChange={(e) => setStartTime(e.target.value)}
                          className="border-slate-600 bg-slate-800"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <Label className="text-slate-300">End Date & Time</Label>
                    <div className="flex space-x-2">
                      <div className="flex-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal border-slate-600 bg-slate-800 hover:bg-slate-700",
                                !endDate && "text-slate-400"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-blue-400" />
                              {endDate ? format(endDate, "PP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 border-slate-700 bg-slate-800">
                            <Calendar
                              mode="single"
                              selected={endDate}
                              onSelect={setEndDate}
                              initialFocus
                              className="bg-slate-800 text-white"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="w-24">
                        <Input 
                          type="time" 
                          value={endTime} 
                          onChange={(e) => setEndTime(e.target.value)}
                          className="border-slate-600 bg-slate-800"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <Label>Duration</Label>
                  <p className="mt-1 text-gray-500">{calculateDuration()}</p>
                </div>
              </div>
            )}
            
            {/* Feeding-specific fields */}
            {type === 'feeding' && (
              <div className="border border-slate-700/50 rounded-lg p-6 bg-slate-800/30 backdrop-blur-sm">
                <h3 className="text-lg font-medium mb-4 text-blue-300 flex items-center gap-2">
                  <Utensils className="h-5 w-5" />
                  Feeding Details
                </h3>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="feedingType" className="text-slate-300">Feeding Type</Label>
                    <Select 
                      value={selectedFeedingType || ''} 
                      onValueChange={setSelectedFeedingType}
                    >
                      <SelectTrigger id="feedingType">
                        <SelectValue placeholder="Select feeding type" />
                      </SelectTrigger>
                      <SelectContent>
                        {feedingTypeOptions?.map(option => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label className="text-slate-300">Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full justify-start text-left font-normal mt-1 border-slate-600 bg-slate-800 hover:bg-slate-700",
                              !startDate && "text-slate-400"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-blue-400" />
                            {startDate ? format(startDate, "PP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 border-slate-700 bg-slate-800">
                          <Calendar
                            mode="single"
                            selected={startDate}
                            onSelect={setStartDate}
                            initialFocus
                            className="bg-slate-800 text-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div className="w-24">
                      <Label className="text-slate-300">Time</Label>
                      <Input 
                        type="time" 
                        value={startTime} 
                        onChange={(e) => setStartTime(e.target.value)}
                        className="mt-1 border-slate-600 bg-slate-800" 
                      />
                    </div>
                  </div>
                  
                  {/* Breastfeeding specific fields */}
                  {selectedFeedingType === 'breastfeeding' && (
                    <div className="border border-slate-700/50 rounded-md p-4 bg-slate-800/50">
                      <h4 className="font-medium mb-3 text-blue-300">Breastfeeding Details</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="breast" className="text-slate-300">Breast</Label>
                          <Select 
                            value={selectedBreast || ''} 
                            onValueChange={setSelectedBreast}
                          >
                            <SelectTrigger id="breast">
                              <SelectValue placeholder="Select breast" />
                            </SelectTrigger>
                            <SelectContent>
                              {breastOptions?.map(option => (
                                <SelectItem key={option.id} value={option.id}>
                                  {option.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="duration" className="text-slate-300">Duration (minutes)</Label>
                          <Input
                            id="duration"
                            type="number"
                            value={feedingDuration}
                            onChange={(e) => setFeedingDuration(e.target.value)}
                            placeholder="Enter duration in minutes"
                            className="border-slate-600 bg-slate-800"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Bottle feeding specific fields */}
                  {selectedFeedingType === 'bottle' && (
                    <div className="border border-slate-700/50 rounded-md p-4 bg-slate-800/50">
                      <h4 className="font-medium mb-3 text-blue-300">Bottle Feeding Details</h4>
                      <div>
                        <Label htmlFor="bottleAmount" className="text-slate-300">Amount (oz)</Label>
                        <Input
                          id="bottleAmount"
                          type="number"
                          value={bottleAmountOz}
                          onChange={(e) => setBottleAmountOz(e.target.value)}
                          placeholder="Enter amount in ounces"
                          className="border-slate-600 bg-slate-800"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Formula feeding specific fields */}
                  {selectedFeedingType === 'formula' && (
                    <div className="border border-slate-700/50 rounded-md p-4 bg-slate-800/50">
                      <h4 className="font-medium mb-3 text-blue-300">Formula Details</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="formulaAmount" className="text-slate-300">Amount (oz)</Label>
                          <Input
                            id="formulaAmount"
                            type="number"
                            value={formulaAmountOz}
                            onChange={(e) => setFormulaAmountOz(e.target.value)}
                            placeholder="Enter amount in ounces"
                            className="border-slate-600 bg-slate-800"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="waterTemp" className="text-slate-300">Water Temperature</Label>
                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <Button
                              type="button"
                              variant={waterTemp === "room" ? "default" : "outline"}
                              className={waterTemp === "room" ? "bg-purple-600 hover:bg-purple-700" : ""}
                              onClick={() => setWaterTemp("room")}
                            >
                              Room Temp
                            </Button>
                            <Button
                              type="button"
                              variant={waterTemp === "warm" ? "default" : "outline"}
                              className={waterTemp === "warm" ? "bg-purple-600 hover:bg-purple-700" : ""}
                              onClick={() => setWaterTemp("warm")}
                            >
                              Warm
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Sleep and Nap fields */}
            {(type === 'sleep' || type === 'nap') && (
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="text-lg font-medium mb-3">Sleep Details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="quality">Quality</Label>
                    <Select 
                      value={selectedQuality || ''} 
                      onValueChange={setSelectedQuality}
                    >
                      <SelectTrigger id="quality">
                        <SelectValue placeholder="Select quality" />
                      </SelectTrigger>
                      <SelectContent>
                        {qualityOptions?.map(option => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Select 
                      value={selectedLocation || ''} 
                      onValueChange={setSelectedLocation}
                    >
                      <SelectTrigger id="location">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locationOptions?.map(option => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="method">Onset Method</Label>
                    <Select 
                      value={selectedMethod || ''} 
                      onValueChange={setSelectedMethod}
                    >
                      <SelectTrigger id="method">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {methodOptions?.map(option => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="environment">Environment</Label>
                    <Select 
                      value={selectedEnvironment || ''} 
                      onValueChange={setSelectedEnvironment}
                    >
                      <SelectTrigger id="environment">
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                      <SelectContent>
                        {environmentOptions?.map(option => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Sleep-specific fields */}
                {type === 'sleep' && (
                  <div className="grid gap-4 sm:grid-cols-2 mt-4">
                    <div>
                      <Label htmlFor="sound">Sound</Label>
                      <Select 
                        value={selectedSound || ''} 
                        onValueChange={setSelectedSound}
                      >
                        <SelectTrigger id="sound">
                          <SelectValue placeholder="Select sound" />
                        </SelectTrigger>
                        <SelectContent>
                          {soundOptions?.map(option => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="soundLevel">Sound Level</Label>
                      <Select 
                        value={selectedSoundLevel || ''} 
                        onValueChange={setSelectedSoundLevel}
                      >
                        <SelectTrigger id="soundLevel">
                          <SelectValue placeholder="Select sound level" />
                        </SelectTrigger>
                        <SelectContent>
                          {soundLevelOptions?.map(option => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Notes field - available for all activity types */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this activity..."
                className="min-h-[120px]"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between py-6 bg-slate-900 border-t border-slate-700/50">
          <Button 
            variant="outline" 
            onClick={handleBackClick}
            className="border-slate-600 hover:bg-slate-700 transition-colors"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveClick}
            disabled={isSubmitting || !timeInputsValid}
            className={cn(
              "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-colors min-w-32",
              (isSubmitting || !timeInputsValid) && "opacity-50 cursor-not-allowed"
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent className="bg-slate-800 border border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Save Changes</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Are you sure you want to save these changes to the {type} activity?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="bg-slate-800">
            <AlertDialogCancel className="bg-slate-700 hover:bg-slate-600 border-none text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSave}
              className="bg-red-700 hover:bg-red-600 text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function formatDateTimeForDB(date: Date, time: string) {
  const [hours, minutes] = time.split(':').map(Number)
  const localDateTime = new Date(date);
  localDateTime.setHours(hours, minutes, 0, 0);
  return dayjs(localDateTime).toISOString();
}
